/**
 * Protocol + bounded ExcelJS parsing core shared by the xlsx import worker
 * (`xlsxImport.worker.ts`) and the main-thread caller
 * (`parseWorkbookBounded.ts`).
 *
 * AUD-007: replaces SheetJS (`xlsx`) with ExcelJS behind hard resource
 * limits — file size, rows, columns, non-empty cells and a wall-clock
 * timeout — so a hostile workbook cannot exhaust the browser tab.
 *
 * ExcelJS is imported dynamically inside `parseWorkbookBuffer` so that
 * importing this module (types, limits, error class) costs nothing in the
 * main bundle.
 */

/** Only primitives ever cross the worker boundary. */
export type CellValue = string | number | boolean | null;

export interface WorkbookLimits {
  /** Maximum file size in bytes (checked before any parsing). */
  maxBytes: number;
  /** Maximum number of rows in the first worksheet. */
  maxRows: number;
  /** Maximum number of columns in the first worksheet. */
  maxColumns: number;
  /** Maximum number of non-empty cells. */
  maxCells: number;
  /** Wall-clock parsing timeout in milliseconds. */
  timeoutMs: number;
}

export const DEFAULT_WORKBOOK_LIMITS: WorkbookLimits = {
  maxBytes: 5 * 1024 * 1024,
  maxRows: 5_000,
  maxColumns: 100,
  maxCells: 100_000,
  timeoutMs: 10_000,
};

export type WorkbookErrorCode =
  | 'FILE_TOO_LARGE'
  | 'ROW_LIMIT_EXCEEDED'
  | 'COLUMN_LIMIT_EXCEEDED'
  | 'CELL_LIMIT_EXCEEDED'
  | 'EMPTY_WORKBOOK'
  | 'PARSE_TIMEOUT'
  | 'PARSE_ERROR';

export class WorkbookParseError extends Error {
  readonly code: WorkbookErrorCode;

  constructor(code: WorkbookErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'WorkbookParseError';
    this.code = code;
  }
}

/** Main thread → worker. */
export interface ParseRequest {
  buffer: ArrayBuffer;
  limits: WorkbookLimits;
}

/** Worker → main thread. */
export type ParseResponse =
  | { ok: true; rows: CellValue[][] }
  | { ok: false; code: WorkbookErrorCode; message: string };

/** Reduce any ExcelJS cell value to a transferable primitive. */
function toPrimitive(value: unknown): CellValue {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') {
    const v = value as {
      richText?: Array<{ text?: string }>;
      text?: unknown;
      result?: unknown;
      formula?: unknown;
      error?: unknown;
    };
    if (Array.isArray(v.richText)) {
      return v.richText.map((r) => r.text ?? '').join('');
    }
    // Formula cell: never interpret the formula, use the cached result only
    // when it is already a primitive.
    if (v.formula !== undefined) {
      const r = toPrimitive(v.result);
      return typeof r === 'object' ? '' : r;
    }
    if (typeof v.text === 'string') return v.text;
  }
  return '';
}

/**
 * Parse the first worksheet of an `.xlsx` buffer into a primitive
 * array-of-arrays, enforcing every limit in {@link WorkbookLimits} except
 * `maxBytes`/`timeoutMs` (owned by the caller).
 *
 * @throws WorkbookParseError with a typed code on any limit violation.
 */
export async function parseWorkbookBuffer(
  buffer: ArrayBuffer,
  limits: WorkbookLimits,
): Promise<CellValue[][]> {
  const mod = await import('exceljs');
  const ExcelJS = mod.default ?? mod;

  const workbook = new ExcelJS.Workbook();
  try {
    await workbook.xlsx.load(buffer);
  } catch (err) {
    throw new WorkbookParseError('PARSE_ERROR', err instanceof Error ? err.message : String(err));
  }

  const worksheet = workbook.worksheets[0];
  if (!worksheet || workbook.worksheets.length === 0) {
    throw new WorkbookParseError('EMPTY_WORKBOOK', 'Workbook has no worksheets');
  }
  if (worksheet.rowCount > limits.maxRows) {
    throw new WorkbookParseError(
      'ROW_LIMIT_EXCEEDED',
      `Worksheet has ${worksheet.rowCount} rows (limit ${limits.maxRows})`,
    );
  }
  if (worksheet.columnCount > limits.maxColumns) {
    throw new WorkbookParseError(
      'COLUMN_LIMIT_EXCEEDED',
      `Worksheet has ${worksheet.columnCount} columns (limit ${limits.maxColumns})`,
    );
  }

  let cellCount = 0;
  const rows: CellValue[][] = [];
  for (let r = 1; r <= worksheet.rowCount; r += 1) {
    const row = worksheet.getRow(r);
    const values: CellValue[] = [];
    for (let c = 1; c <= worksheet.columnCount; c += 1) {
      const primitive = toPrimitive(row.getCell(c).value);
      if (primitive !== '' && primitive !== null) {
        cellCount += 1;
        if (cellCount > limits.maxCells) {
          throw new WorkbookParseError(
            'CELL_LIMIT_EXCEEDED',
            `Worksheet exceeds ${limits.maxCells} non-empty cells`,
          );
        }
      }
      values.push(primitive);
    }
    rows.push(values);
  }
  return rows;
}
